import React, { useState, useRef, useEffect } from "react";
import { Crop, X, Check } from "lucide-react";

const MAX_DISPLAY = 420;
const OUTPUT_SIZE = 512;

export default function ImageCropper({ file, onCancel, onCrop }) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [image, setImage] = useState(null);
  const [imgDim, setImgDim] = useState({ w: 0, h: 0, naturalW: 0, naturalH: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0, size: 0 });
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      let w, h;
      if (img.width >= img.height) {
        w = Math.min(MAX_DISPLAY, img.width);
        h = (img.height / img.width) * w;
      } else {
        h = Math.min(MAX_DISPLAY, img.height);
        w = (img.width / img.height) * h;
      }
      setImgDim({ w, h, naturalW: img.width, naturalH: img.height });
      const size = Math.min(w, h) * 0.85;
      setCrop({
        x: (w - size) / 2,
        y: (h - size) / 2,
        size,
      });
      imgRef.current = img;
      setImage(url);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!drag) return;
    const handleMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const orig = drag.origCrop;
      if (drag.type === "move") {
        let nx = Math.max(0, Math.min(orig.x + dx, imgDim.w - orig.size));
        let ny = Math.max(0, Math.min(orig.y + dy, imgDim.h - orig.size));
        setCrop({ ...orig, x: nx, y: ny });
      } else if (drag.type === "resize") {
        const delta = Math.max(dx, dy);
        let newSize = Math.max(40, orig.size + delta);
        const maxSize = Math.min(imgDim.w - orig.x, imgDim.h - orig.y);
        newSize = Math.min(newSize, maxSize);
        setCrop({ ...orig, size: newSize });
      }
    };
    const handleUp = () => setDrag(null);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [drag, imgDim]);

  const startDrag = (e, type) => {
    e.stopPropagation();
    e.preventDefault();
    setDrag({
      type,
      startX: e.clientX,
      startY: e.clientY,
      origCrop: { ...crop },
    });
  };

  const handleCrop = () => {
    const scale = imgDim.naturalW / imgDim.w;
    const sx = crop.x * scale;
    const sy = crop.y * scale;
    const sSize = crop.size * scale;
    const outSize = Math.min(OUTPUT_SIZE, Math.round(sSize));

    const canvas = document.createElement("canvas");
    canvas.width = outSize;
    canvas.height = outSize;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, outSize, outSize);

    canvas.toBlob(
      (blob) => {
        const croppedFile = new File([blob], "avatar.jpg", { type: "image/jpeg" });
        onCrop(croppedFile);
      },
      "image/jpeg",
      0.9
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Crop className="w-4 h-4 text-blue-600" />
            <h3 className="font-bold text-gray-900 text-sm">Crop Avatar (1:1)</h3>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 flex flex-col items-center">
          {image && imgDim.w > 0 && (
            <div
              ref={containerRef}
              className="relative overflow-hidden rounded-lg select-none"
              style={{ width: imgDim.w, height: imgDim.h }}
            >
              <img src={image} alt="crop preview" className="pointer-events-none" draggable={false} />

              {/* Dark overlay outside crop via box-shadow */}
              <div
                className="absolute cursor-move"
                style={{
                  left: crop.x,
                  top: crop.y,
                  width: crop.size,
                  height: crop.size,
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
                  border: "2px solid white",
                }}
                onMouseDown={(e) => startDrag(e, "move")}
              >
                {/* Grid lines */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/3 left-0 right-0 border-t border-white/30" />
                  <div className="absolute top-2/3 left-0 right-0 border-t border-white/30" />
                  <div className="absolute left-1/3 top-0 bottom-0 border-l border-white/30" />
                  <div className="absolute left-2/3 top-0 bottom-0 border-l border-white/30" />
                </div>
                {/* Resize handle */}
                <div
                  className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-white rounded-full shadow border-2 border-blue-500 cursor-nwse-resize"
                  onMouseDown={(e) => startDrag(e, "resize")}
                />
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-3 text-center">
            Drag the square to reposition. Drag the corner handle to resize.
          </p>
        </div>

        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={onCancel}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-1.5"
          >
            <Check className="w-4 h-4" />
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}